/**
 * Is the app in inventory mode? Asked once, from the server.
 *
 * In inventory mode an uploaded script goes to the placement agent and the creator gets an
 * acknowledgement; the Artie Score is not computed. That matters to any screen that WAITS for a
 * score, because there is no longer one coming — NewProjectLoading polls until score data arrives
 * and ScorePendingPage polls every five seconds for the project to be marked reviewed. Both would
 * spin forever.
 *
 * The answer comes from the server rather than a VITE_ variable. The backend flag already decides
 * the behaviour; a second copy of that decision in the frontend's build config is a copy that can
 * disagree with it, and the symptom of disagreement is a creator stuck on a loading screen.
 *
 * Asked in ONE place on purpose. There are ten calls to uploadScript across six files — the signup
 * wizard, the new-project wizard and the intake form were each built separately — so a branch
 * threaded through the upload sites would eventually miss one.
 *
 * The waiting screens are NOT the choke point, which is what the first version of this assumed. Four
 * screens were guarded by hand and a fifth upload flow (/projects/new -> /projects/new-details ->
 * ProjectDetailsPage) went unguarded anyway; fourteen files in total reach for a score. The real
 * choke point is use-artie-score.ts, which twelve of them go through, so that is where the work is
 * actually stopped. This hook is the fact; that hook is the enforcement.
 *
 * `null` means "not known yet", which is distinct from `false`. A screen must not act on the answer
 * before it has one, or it will flash the wrong destination on every load.
 */
import { useEffect, useState } from "react";

// Built from VITE_API_BASE_URL exactly like every other call in the app (lib/api.ts, use-artie-score's
// SSE URL, use-project-updates). This was a bare relative "/api/v1/placements/mode", which works in
// development for one reason only: vite.config.ts proxies /api to localhost:8000. The deployed
// frontend is a static site on its own origin with no such rewrite, so in production that path
// resolved against the STATIC SITE, returned the index.html or a 404, and the fetch below failed on
// every single load. The old catch block then answered "not inventory mode", so all four waiting
// screens silently reverted to the Artie Score flow this work exists to replace — the feature would
// have looked fine locally and done nothing at all on Render.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const ENDPOINT = `${BASE_URL}/api/v1/placements/mode`;

/**
 * Cached once ANSWERED, because the server's flag cannot change without a restart.
 *
 * The cache belongs to the SIGNED-IN SESSION, not the page load. Once PLACEMENT_AGENT_ALLOWLIST is
 * set the answer depends on WHO is asking, and this is a single-page app: signing out and back in as
 * a different creator never reloads the module, so a cached `true` from an allowlisted account would
 * follow the next one to a thank-you page whose inventory run was never queued — and their Artie
 * Score, which did run, would sit there unread. resetInventoryMode() exists for exactly that, and
 * authStore calls it on both login and logout.
 */
let cached: boolean | null = null;

/** Forget the cached answer. Called whenever the signed-in account changes. */
export function resetInventoryMode(): void {
  cached = null;
  inFlight = null;
}

/**
 * How many times to ask before giving up. A failed check is not a neutral event (see below), so it is
 * worth a couple of retries before falling back to a guess.
 */
const ATTEMPTS = 3;

/**
 * Ask the server, with retries. Exported for callers that are not React components — see
 * lib/api.ts, where the score POST itself is refused.
 *
 * In-flight requests are shared, so ten screens mounting at once make one request, not ten.
 */
let inFlight: Promise<boolean> | null = null;

export async function fetchInventoryMode(): Promise<boolean> {
  if (cached !== null) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      for (let attempt = 1; attempt <= ATTEMPTS; attempt += 1) {
        try {
          const response = await fetch(ENDPOINT, { credentials: "include" });
          if (!response.ok) throw new Error(String(response.status));
          const body = await response.json();
          cached = Boolean(body?.inventory_mode);
          return cached;
        } catch {
          if (attempt < ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
          }
        }
      }

      // FAILING CLOSED, and the old code had this backwards. It fell back to false and called that
      // "the safe default", reasoning that a creator should see the flow they saw yesterday. But the
      // two wrong answers are not symmetric, because the backend guard is what actually decides:
      //
      //   guess false, truth true  -> the score task is skipped server-side, so the score NEVER
      //                               arrives and the creator watches a loading screen forever.
      //   guess true,  truth false -> the creator sees a thank-you page instead of a score. Wrong,
      //                               but finished, and a reload once the API answers fixes it.
      //
      // A permanent spinner is the worse failure, so an unanswerable check assumes inventory mode.
      // Deliberately NOT written to `cached`: a network blip must not pin this guess for the whole
      // session, so the next caller asks again.
      return true;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function useInventoryMode(): boolean | null {
  const [mode, setMode] = useState<boolean | null>(cached);

  useEffect(() => {
    if (cached !== null) return;
    let alive = true;
    fetchInventoryMode().then((answer) => {
      if (alive) setMode(answer);
    });
    return () => {
      alive = false;
    };
  }, []);

  return mode;
}
